import React, { useState, useEffect } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import CloseIcon24 from "../../../../assets/svg/icons/common/CloseIcon24";
import useCommonStore from "../../../../stores/common";
import useChargebackStore, {
  fetchCBFilterData,
} from "../../../../stores/chargeback";
import useDomainStore from "../../../../stores/domain";
import useUserStore from "../../../../stores/user";
import TagInput from "../../../../components/form/tag-input";
import TextInput from "../../../../components/form/text-input";
import Select from "../../../../components/form/select";
import standardCurrencyFormatter from "../../../../utils/standard-currency-formatter";
import styles from "./styles.module.scss";
import RangeInput from "../../../../components/form/range-input";
import Checkbox from "../../../../components/Checkbox/Index";
import { SearchOrganizations } from "../../../../../api";
import { useSavedFilters } from "../../../../hooks/useSavedFilters";
import SavedFiltersSection from "../../../../components/SavedFiltersSection/SavedFiltersSection";
import { isChargebackFilterSnapshotEmpty } from "../../../../utils/savedFilterSnapshotEmpty";
import { isEntitySuperAdmin } from "../../../../../components/helper/helper";
import Button from "../../../../components/new-button";

const SAVED_FILTER_PAGE = "chargebacks";

const cards = [
  { id: "visa", label: "Visa", icon: "/images/visa24x24.svg" },
  { id: "mastercard", label: "MasterCard", icon: "/images/master24x24.svg" },
  { id: "discover", label: "Discover", icon: "/images/discover24x24.svg" },
  { id: "amex", label: "AmEx", icon: "/images/amex24x24.svg" },
  { id: "debit", label: "Debit", icon: "/images/debit24x24.svg" },
  { id: "ebt", label: "EBT", icon: "/images/ebt24x24.svg" },
];

const cards_mapping = [
  { id: "VD", name: "Visa Debit", icon: "/images/visa24x24.svg" },
  { id: "MD", name: "MasterCard Debit", icon: "/images/master24x24.svg" },
  { id: "AM", name: "American Express", icon: "/images/amex24x24.svg" },
  { id: "VS", name: "Visa Credit", icon: "/images/visa24x24.svg" },
  { id: "MC", name: "MasterCard Credit", icon: "/images/master24x24.svg" },
  { id: "DS", name: "Discover", icon: "/images/discover24x24.svg" },
  { id: "MB", name: "Maestro", icon: "/images/debit24x24.svg" },
  { id: "VB", name: "Visa Business", icon: "/images/visa24x24.svg" },
];

const reasonOptions = [
  { value: "132", label: "132 - Consumer - Cancelled Recurring Transaction" },
  { value: "127", label: "127 - Consumer - Fraudulent Multiple Transactions" },
  { value: "137", label: "137 - Consumer - Credit Not Processed" },
  { value: "154", label: "154 - Consumer - Goods/Services Not Received" },
  { value: "155", label: "155 - Consumer - Goods/Services Cancelled" },
  { value: "158", label: "158 - Consumer - Authorization Error" },
  { value: "167", label: "167 - Consumer - Duplicate Processing" },
  { value: "170", label: "170 - Consumer - Services Not Rendered" },
  { value: "173", label: "173 - Consumer - Credit Posted as Sale" },
];

const FilterDrawer = () => {
  const isMobile = useMediaQuery(768);
  const isSmallMobile = useMediaQuery(420);
  const { isKurvDomain } = useDomainStore();
  const { filterDrawerOpen, setFilterDrawerOpen } = useCommonStore();
  const { filters = { reasons: [] }, setFilters } = useChargebackStore(
    (state) => ({
      filters: state.filters || { reasons: [] },
      setFilters: state.setFilters,
    }),
  );
  const user = useUserStore((state) => state.user);

  const filterData = useChargebackStore((state) => state.filterData);

  const [selectedCards, setSelectedCards] = useState([]);
  const [amount, setAmount] = useState({ from: "", to: "" });
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [cbAmount, setCbAmount] = useState({ from: 0, to: 0 });
  const [daysToRebut, setDaysToRebut] = useState({ from: 0, to: 0 });
  const [isDaysToRebutNull, setIsDaysToRebutNull] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [orgFilter, setOrgFilter] = useState("");

  const handleCardToggle = (cardId) => {
    setSelectedCards((prev) => {
      if (prev.includes(cardId)) {
        return prev.filter((id) => id !== cardId);
      }
      return [...prev, cardId];
    });
  };

  const handleClear = () => {
    setSelectedCards([]);
    setAmount({ from: "", to: "" });
    setDaysToRebut({ from: "", to: "" });
    setIsDaysToRebutNull(false);
    setSelectedReasons([]);
    setOrgFilter("");
  };

  const getActiveFilters = () => {
    const activeFilters = [];

    selectedCards.forEach((card) => {
      const cardInfo = cards.find((c) => c.id === card);
      if (cardInfo) {
        activeFilters.push({ type: "card", value: cardInfo.label, id: card });
      }
    });

    if (amount.from && amount.to) {
      activeFilters.push({
        type: "amount",
        value: `Amount $${amount.from} - $${amount.to}`,
        id: "amount",
      });
    }

    if (daysToRebut.from && daysToRebut.to) {
      activeFilters.push({
        type: "days",
        value: `Days to rebut ${daysToRebut.from} - ${daysToRebut.to}`,
        id: "days",
      });
    }

    if (isDaysToRebutNull) {
      activeFilters.push({
        type: "daysToRebutNull",
        value: "Days to rebut passed",
        id: "daysToRebutNull",
      });
    }

    // Add selected reasons to active filters
    if (selectedReasons && selectedReasons.length > 0) {
      selectedReasons.forEach((reason) => {
        activeFilters.push({
          type: "reason",
          value: reason.label,
          id: reason.value,
        });
      });
    }

    // Add organization filter to active filters
    if (orgFilter) {
      const selectedOrg = organizations.find((org) => org.value === orgFilter);
      if (selectedOrg) {
        activeFilters.push({
          type: "organization",
          value: selectedOrg.label,
          id: "organization",
        });
      }
    }

    return activeFilters;
  };

  const removeFilter = (filter) => {
    if (filter.type === "card") {
      setSelectedCards((prev) => prev.filter((id) => id !== filter.id));
    } else if (filter.type === "days") {
      setDaysToRebut({ from: "", to: "" });
    } else if (filter.type === "daysToRebutNull") {
      setIsDaysToRebutNull(false);
    } else if (filter.type === "amount") {
      setAmount({ from: "", to: "" });
    } else if (filter.type === "reason") {
      setFilters({
        ...filters,
        reasons: filters.reasons.filter((reason) => reason.value !== filter.id),
      });
    } else if (filter.type === "organization") {
      setOrgFilter("");
    }
  };

  const handleReasonChange = (reasons) => {
    setSelectedReasons(reasons);
  };

  useEffect(() => {
    fetchCBFilterData();

    // Fetch organizations on mount
    SearchOrganizations(null).then((res) => {
      if (!res.success) {
        console.error("There was a problem fetching organizations");
        return;
      }
      const orgsOptions = res?.data?.map((org) => ({
        label: org.name,
        value: org.id,
      }));
      setOrganizations(orgsOptions);
    });
  }, []);

  useEffect(() => {
    setCbAmount({
      from: filterData.min_cb_amount,
      to: filterData.max_cb_amount,
    });

    setDaysToRebut({
      from: filterData.min_days_to_rebut,
      to: filterData.max_days_to_rebut,
    });
  }, [filterData]);

  const handleOnSearchInputChange = async (term) => {
    try {
      const res = await SearchOrganizations({ term });
      if (!res.success) {
        console.error("There was a problem fetching organizations");
        return;
      }

      const orgsOptions = res?.data.map((org) => ({
        label: org.name,
        value: org.id,
      }));

      setOrganizations(orgsOptions);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const handleOnOrganizationSelectChange = (selectedOption) => {
    const value =
      typeof selectedOption === "object"
        ? selectedOption.value
        : selectedOption;
    setOrgFilter(value);
  };

  const getCurrentFilterValues = () => ({
    cards: selectedCards,
    cbAmount,
    daysToRebut,
    isDaysToRebutNull,
    reasons: selectedReasons,
    orgFilter,
  });

  const applyFilterValues = (filters) => {
    setSelectedCards(filters.cards || []);
    setCbAmount(filters.cbAmount || { from: 0, to: 0 });
    setDaysToRebut(filters.daysToRebut || { from: 0, to: 0 });
    setIsDaysToRebutNull(filters.isDaysToRebutNull ?? true);
    setSelectedReasons(filters.reasons || []);
    setOrgFilter(filters.orgFilter || "");
  };

  const getAppliedSnapshot = () => {
    const f = useChargebackStore.getState().filters;
    const num = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    return {
      cards: [...(f.cards || [])],
      cbAmount: {
        from: num(f.amount?.from),
        to: num(f.amount?.to),
      },
      daysToRebut: {
        from: num(f.daysToRebut?.from),
        to: num(f.daysToRebut?.to),
      },
      isDaysToRebutNull: f.isDaysToRebutNull ?? true,
      reasons: [...(f.reasons || [])],
      orgFilter: f.orgFilter || "",
    };
  };

  const savedFiltersApi = useSavedFilters({
    page: SAVED_FILTER_PAGE,
    drawerOpen: filterDrawerOpen,
    getFilterSnapshot: getCurrentFilterValues,
    applyFilterSnapshot: applyFilterValues,
    isSnapshotEmpty: (snapshot) =>
      isChargebackFilterSnapshotEmpty(snapshot, filterData),
    getAppliedSnapshot,
  });

  const onApplyClick = () => {
    setFilters({
      ...filters,
      cards: selectedCards,
      amount: cbAmount,
      daysToRebut,
      isDaysToRebutNull,
      reasons: selectedReasons,
      orgFilter: orgFilter,
    });
    savedFiltersApi.commitAppliedSavedFilter();
    setFilterDrawerOpen(false);
  };

  return (
    <Drawer
      open={filterDrawerOpen}
      onClose={() => setFilterDrawerOpen(false)}
      direction={isMobile ? "left" : "right"}
      className={styles.filter_drawer}
      size={isSmallMobile ? "320px" : isMobile ? "375px" : "440px"}
      zIndex={8888}
      overlayColor={"#000"}
      overlayOpacity={0.5}
    >
      <div
        className={`${styles.drawer_container} ${isKurvDomain ? styles.kurv_theme : ""}`}
      >
        <div className={styles.drawer_title_block}>
          <div className={styles.head}>
            <ButtonIcon onClick={() => setFilterDrawerOpen(false)}>
              <CloseIcon24 />
            </ButtonIcon>
          </div>

          <div className={styles.header}>
            <h5>Filter</h5>
            {getActiveFilters().length > 0 && (
              <div className={styles.active_filters}>
                {getActiveFilters().map((filter, index) => (
                  <div key={index} className={styles.filter_pill}>
                    {filter.value}
                    <button onClick={() => removeFilter(filter)}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.content}>
          <SavedFiltersSection
            savedFilterOptions={savedFiltersApi.savedFilterOptions}
            selectedSavedFilter={savedFiltersApi.selectedSavedFilter}
            onSavedFilterChange={savedFiltersApi.handleSavedFilterSelect}
            onDeleteSavedFilter={savedFiltersApi.handleDeleteSavedFilter}
            isDeletingFilter={savedFiltersApi.isDeletingFilter}
            editFilterName={savedFiltersApi.editFilterName}
            onEditFilterNameChange={savedFiltersApi.setEditFilterName}
            onUpdateSavedFilter={savedFiltersApi.handleUpdateSavedFilter}
            isUpdatingFilter={savedFiltersApi.isUpdatingFilter}
            showSaveFilterInput={savedFiltersApi.showSaveFilterInput}
            onShowSaveInput={savedFiltersApi.openSaveFilterInput}
            newFilterName={savedFiltersApi.newFilterName}
            onNewFilterNameChange={savedFiltersApi.setNewFilterName}
            onSaveFilter={savedFiltersApi.handleSaveFilter}
            isSavingFilter={savedFiltersApi.isSavingFilter}
            onCancelNewFilter={() => {
              savedFiltersApi.cancelSaveFilterInput();
              savedFiltersApi.setNewFilterName("");
            }}
          />

          {isEntitySuperAdmin(user) && (
            <div className={styles.section}>
              <div className={styles.title}>ORGANIZATION</div>
              <div className={styles.organization_container}>
                <Select
                  async
                  search
                  options={organizations}
                  value={orgFilter}
                  onSearchChange={handleOnSearchInputChange}
                  onChange={handleOnOrganizationSelectChange}
                  placeholder="Search organizations"
                />
              </div>
            </div>
          )}

          <div className={styles.section}>
            <h6 className={styles.title}>CARDS</h6>
            <div className={styles.checkbox_group}>
              {cards_mapping.map((card) => (
                <div key={card.id} className={styles.checkbox_item}>
                  <input
                    type="checkbox"
                    id={card.id}
                    checked={selectedCards.includes(card.id)}
                    onChange={() => handleCardToggle(card.id)}
                  />
                  <label htmlFor={card.id} className={styles.card_label}>
                    <img src={card.icon} alt={card.name} />
                    {card.name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.field_container}>
            <div className={styles.field_header}>
              <h6>CB AMOUNT</h6>
            </div>
            <div
              className={`${styles.field_body} ${styles.flexDirectionColumn} ${styles.noGap}`}
            >
              <div className={styles.formGroupRow}>
                <div className={styles.field_input}>
                  <TextInput
                    type="text"
                    name="cbAmountFrom"
                    label="Amount from"
                    placeholder="0.00"
                    value={standardCurrencyFormatter(cbAmount.from)}
                    disabled={false}
                    prefix="$"
                    cleaveOptions={{
                      numeral: true,
                      numeralDecimalMark: ".",
                      numeralDecimalScale: 2,
                      numeralThousandsGroupStyle: "thousand",
                      numeralPositiveOnly: true,
                      onValueChanged: (e) => {
                        setCbAmount((prev) => ({
                          ...prev,
                          from: parseFloat(e.target.rawValue) || 0,
                        }));
                      },
                    }}
                  />
                </div>

                <div className={styles.field_input}>
                  <TextInput
                    type="text"
                    name="cbAmountTo"
                    label="Amount to"
                    placeholder="0.00"
                    value={standardCurrencyFormatter(cbAmount.to)}
                    disabled={false}
                    prefix="$"
                    cleaveOptions={{
                      numeral: true,
                      numeralDecimalMark: ".",
                      numeralDecimalScale: 2,
                      numeralThousandsGroupStyle: "thousand",
                      numeralPositiveOnly: true,
                      onValueChanged: (e) => {
                        setCbAmount((prev) => ({
                          ...prev,
                          to: parseFloat(e.target.rawValue) || 0,
                        }));
                      },
                    }}
                  />
                </div>
              </div>

              <div className={styles.rangeContainer} style={{ width: "100%" }}>
                <RangeInput
                  disabled={false}
                  min={filterData?.min_cb_amount || 0}
                  max={filterData.max_cb_amount || 10000}
                  value={[cbAmount.from, cbAmount.to]}
                  onChange={(value) => {
                    setCbAmount({
                      from: value[0],
                      to: value[1],
                    });
                  }}
                />
              </div>
            </div>
          </div>

          <div className={styles.field_container}>
            <div className={styles.field_header}>
              <h6>DAYS TO REBUT</h6>
            </div>
            <div
              className={`${styles.field_body} ${styles.flexDirectionColumn} ${styles.noGap}`}
            >
              <div className={styles.formGroupRow}>
                <div className={styles.field_input}>
                  <TextInput
                    type="text"
                    name="daysToRebutFrom"
                    label="Days from"
                    placeholder="0"
                    value={daysToRebut.from.toString()}
                    disabled={false}
                    cleaveOptions={{
                      numeral: true,
                      numeralDecimalMark: ".",
                      numeralDecimalScale: 0,
                      numeralThousandsGroupStyle: "thousand",
                      numeralPositiveOnly: true,
                      onValueChanged: (e) => {
                        setDaysToRebut((prev) => ({
                          ...prev,
                          from: parseInt(e.target.rawValue) || 0,
                        }));
                      },
                    }}
                  />
                </div>

                <div className={styles.field_input}>
                  <TextInput
                    type="text"
                    name="daysToRebutTo"
                    label="Days to"
                    placeholder="0"
                    value={daysToRebut.to.toString()}
                    disabled={false}
                    cleaveOptions={{
                      numeral: true,
                      numeralDecimalMark: ".",
                      numeralDecimalScale: 0,
                      numeralThousandsGroupStyle: "thousand",
                      numeralPositiveOnly: true,
                      onValueChanged: (e) => {
                        setDaysToRebut((prev) => ({
                          ...prev,
                          to: parseInt(e.target.rawValue) || 0,
                        }));
                      },
                    }}
                  />
                </div>
              </div>

              <div className={styles.rangeContainer} style={{ width: "100%" }}>
                <RangeInput
                  disabled={false}
                  min={filterData.min_days_to_rebut || 0}
                  max={filterData.max_days_to_rebut || 100}
                  value={[daysToRebut.from, daysToRebut.to]}
                  onChange={(value) => {
                    setDaysToRebut({
                      from: value[0],
                      to: value[1],
                    });
                  }}
                />
              </div>
              <div className={styles.filter_option_row}>
                <Checkbox
                  id="daysToRebutNull"
                  name="daysToRebutNull"
                  value="daysToRebutNull"
                  checked={isDaysToRebutNull}
                  onChange={(e) => setIsDaysToRebutNull(e.target.checked)}
                />
                <label htmlFor="daysToRebutNull" className={styles.filter_option_label}>
                  Days to rebut passed
                </label>
              </div>
            </div>
          </div>

          <div className={styles.field_container}>
            <div className={styles.field_header}>
              <h6>REASON</h6>
            </div>
            <div className={styles.field_body}>
              <div className={styles.form_group}>
                <TagInput
                  id="reasons"
                  name="reasons"
                  options={filterData?.cb_reasons}
                  value={selectedReasons}
                  onChange={handleReasonChange}
                  placeholder="Search"
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" size="large" onClick={handleClear}>
            Clear
          </Button>
          <Button variant="primary" size="large" onClick={onApplyClick}>
            Apply
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default FilterDrawer;
